import { z } from 'zod'

/**
 * RBAC (Role-Based Access Control) type definitions
 * Based on ../cp-backend/docs/frontend-rbac-requirements.md
 */

export const Permission = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().optional(),
  resource: z.string().optional(),
  action: z.string().optional()
})
export type Permission = z.infer<typeof Permission>

export const Role = z.object({
  id: z.number(),
  name: z.string(),
  display_name: z.string(),
  description: z.string().optional(),
  priority: z.number(),
  system: z.boolean().default(false),
  permissions: z.array(z.string()).optional()
})
export type Role = z.infer<typeof Role>

export const AccountRole = z.object({
  role_id: z.number(),
  role_name: z.string(),
  display_name: z.string(),
  description: z.string().optional(),
  granted_at: z.string(),
  granted_by: z.string().nullable().optional(),
  expires_at: z.string().nullable().optional()
})
export type AccountRole = z.infer<typeof AccountRole>

export const Delegation = z.object({
  id: z.number(),
  domain_id: z.number(),
  domain_name: z.string(),
  delegator_id: z.number(),
  delegator_email: z.string(),
  permissions: z.array(z.string()),
  granted_at: z.string(),
  expires_at: z.string().nullable().optional()
})
export type Delegation = z.infer<typeof Delegation>

export const ManageableDomain = z.object({
  domain_id: z.number(),
  domain_name: z.string(),
  is_owner: z.boolean(),
  permissions: z.array(z.string()),
  delegated_by: z.string().nullable().optional()
})
export type ManageableDomain = z.infer<typeof ManageableDomain>

export const ManageableSite = z.object({
  site_name: z.string(),
  status: z.string(),
  is_owner: z.boolean(),
  permissions: z.array(z.string()),
  delegated_by: z.string().nullable().optional()
})
export type ManageableSite = z.infer<typeof ManageableSite>

export const Delegate = z.object({
  id: z.number(),
  type: z.enum(['account', 'mailbox']).default('account'),
  account_id: z.number().optional().nullable(),
  email: z.string(),
  permissions: z.array(z.string()),
  granted_at: z.string(),
  expires_at: z.string().nullable().optional()
})
export type Delegate = z.infer<typeof Delegate>

export const DelegationPreset = z.object({
  name: z.string(),
  label: z.string(),
  description: z.string().optional(),
  permissions: z.array(z.string())
})
export type DelegationPreset = z.infer<typeof DelegationPreset>

export const PermissionCheckResult = z.object({
  allowed: z.boolean(),
  reason: z.string().optional()
})
export type PermissionCheckResult = z.infer<typeof PermissionCheckResult>

export const AccountPermissions = z.object({
  roles: z.array(z.string()),
  permissions: z.array(z.string()),
  is_admin: z.boolean()
})
export type AccountPermissions = z.infer<typeof AccountPermissions>

export interface PermissionCheckInput {
  permission: string
  domainId?: number
  mode?: 'hide' | 'disable'
}

export const DOMAIN_PERMISSIONS = [
  'dns:read',
  'dns:write',
  'dyndns:read',
  'dyndns:write',
  'mailbox:read',
  'mailbox:write',
  'mailbox:delete',
  'mailinglist:read',
  'mailinglist:write',
  'alias:read',
  'alias:write',
  'alias:delete'
] as const

export type DomainPermission = (typeof DOMAIN_PERMISSIONS)[number]

export const ADMIN_PERMISSIONS = [
  'account:list',
  'account:impersonate',
  'account:split',
  'domain:list',
  'domain:suspend',
  'role:read',
  'role:manage',
  'compliance:view',
  'disclosure:read',
  'abuse:read',
  'fulfillment:manage',
  'import:domain',
  'storage:suspend'
] as const

export type AdminPermission = (typeof ADMIN_PERMISSIONS)[number]

/**
 * Customer permissions the panel gates single actions on. Reading a
 * credential (database password, config snippet, SFTP login, kubeconfig), a
 * backup or a past job's output takes the write level, and deleting a VServer
 * its own delete permission, so a read-only or editing team member does not
 * see actions the backend would refuse.
 */
export const ACTION_PERMISSIONS = {
  siteCredentials: 'hosting:sites:write',
  jobLogs: 'hosting:sites:write',
  backupDownload: 'hosting:backup:write',
  kubeconfig: 'k8s:config:write',
  vserverDelete: 'vserver:delete'
} as const
